import * as THREE from 'three';
import CruciblePiece from '../cruciblePiece';
import { GLTF } from 'three/examples/jsm/loaders/GLTFLoader';
import CrucibleLoader from '../../crucibleLoader';
import extras from './extras.json';

export class ArcaneRings extends CruciblePiece {
  protected _rotationBaseSpeed: number;
  protected _color: THREE.Color;
  protected _renderer: THREE.WebGLRenderer;
  protected _loader: CrucibleLoader;

  constructor(
    color: THREE.Color,
    renderer: THREE.WebGLRenderer,
    loader: CrucibleLoader
  ) {
    super();
    this._rotationBaseSpeed = 0.001;
    this._renderer = renderer;
    this._color = color;
    this._loader = loader;
  }

  async setup(gltf: GLTF) {
    this.group = gltf.scene;
    this.group.scale.set(2, 2, 2); // This should be removed with final models
    this.group.position.y -= 1.2;

    let baseColorTexture: THREE.Texture = await this._loader.loadTexture(
      extras.arcaneRings.baseColorTexture,
      true
    );
    baseColorTexture.flipY = false;
    baseColorTexture.wrapS = THREE.RepeatWrapping;
    baseColorTexture.wrapT = THREE.RepeatWrapping;
    baseColorTexture.repeat.set(1, 1);

    let emissiveTexture: THREE.Texture = await this._loader.loadTexture(
      extras.arcaneRings.emissiveTexture,
      true
    );
    emissiveTexture.flipY = false;
    emissiveTexture.wrapS = THREE.RepeatWrapping;
    emissiveTexture.wrapT = THREE.RepeatWrapping;
    emissiveTexture.repeat.set(1, 1);

    const newMaterial: THREE.MeshStandardMaterial =
      this._getArcaneRingsMaterial(baseColorTexture, emissiveTexture);

    this.group.traverse((node: any) => {
      if (node.isMesh) {
        node.castShadow = true;
        node.receiveShadow = true;
        if (node.material.map) node.material.map.anisotropy = 16;
        if (node.material) {
          node.material = newMaterial;
        }
      }
    });
  }

  animate(timeElapsed: number) {
    if (this.group.children.length > 0) {
      this.group.children[0].children.forEach((dustRing: THREE.Object3D, i) => {
        dustRing.rotation.y += this._rotationBaseSpeed * ((i + 1) * -1);
      });
    }
  }

  _hueShift(h: number, amount: number) {
    h += amount;
    if (h >= 1) h -= 1;
    if (h < 0) h += 1;
    return h;
  }

  _getArcaneRingsMaterial(
    diffuseTexture: THREE.Texture,
    emissiveTexture: THREE.Texture
  ) {
    const gradientColor1 = this._color;

    const mainColorHsl: any = {};
    gradientColor1.getHSL(mainColorHsl);

    const gradientColor2 = new THREE.Color().setHSL(
      this._hueShift(mainColorHsl.h, 0.5),
      mainColorHsl.s,
      mainColorHsl.l
    );

    let uniforms = {
      color1: { value: gradientColor1 },
      color2: { value: gradientColor2 }
    };

    let arcaneRingsMaterial = new THREE.MeshStandardMaterial({
      map: diffuseTexture,
      color: new THREE.Color(0xffffff),
      emissive: new THREE.Color(0xffffff),
      emissiveIntensity: 2.3,
      emissiveMap: emissiveTexture,
      alphaMap: emissiveTexture,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      transparent: true,
      side: THREE.DoubleSide,
      //@ts-ignore
      onBeforeCompile: (shader) => {
        shader.uniforms.color1 = uniforms.color1;
        shader.uniforms.color2 = uniforms.color2;

        shader.fragmentShader = `
            uniform vec3 color1;
            uniform vec3 color2;

            ${shader.fragmentShader}
          `
          .replace(
            `#include <map_fragment>`,
            `
              #include <map_fragment>
              #ifdef USE_MAP
                vec4 sampledDiffuse = texture2D( map, vUv );
                vec3 rampColor = mix(color1, color2, sampledDiffuse.g); 
                diffuseColor = vec4( rampColor, diffuseColor.a );
              #endif
            `
          )
          .replace(
            `#include <emissivemap_fragment>`,
            `#include <emissivemap_fragment>

            #ifdef USE_EMISSIVEMAP
              vec4 sampledEmissive = texture2D( emissiveMap, vUv );
              vec3 rampEmissive = mix(color1, color2, sampledEmissive.g); 
              totalEmissiveRadiance *= rampEmissive.rgb;
            #endif

            `
          );
      }
    });

    return arcaneRingsMaterial;
  }
}
